import {useMemo} from 'react'

import {groupChatMessages} from '../components/ChatMessagesGroup'
import type {CopilotChatState} from '../utils/copilot-chat-reducer'
import {getActiveMessages} from '../utils/copilot-chat-subthreading-helpers'
import {type CopilotChatMessage, NullMessageId} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {fuseAssistantMessages} from '../utils/tool-grouping'

type GroupedMessagesState = Pick<
  CopilotChatState,
  'streamingMessage' | 'isWaitingOnCopilot' | 'messages' | 'selectedThreadID' | 'pendingFirstMessage'
>

export function useGroupedMessages(state: GroupedMessagesState) {
  const {
    streamingMessage,
    isWaitingOnCopilot,
    messages: completedMessages,
    selectedThreadID,
    pendingFirstMessage,
  } = state

  const messages = useMemo(() => {
    const nullMessage: CopilotChatMessage = {
      id: NullMessageId,
      role: 'assistant',
      content: '',
      createdAt: '',
      threadID: selectedThreadID || '',
      references: [],
      clientSide: true,
      messageIndex: -1,
    }

    const activeMessages = getActiveMessages(completedMessages, pendingFirstMessage)

    const withStreaming = isWaitingOnCopilot ? [...activeMessages, streamingMessage ?? nullMessage] : activeMessages

    return copilotFeatureFlags.interspersedToolCalls ? fuseAssistantMessages(withStreaming) : withStreaming
  }, [selectedThreadID, completedMessages, pendingFirstMessage, isWaitingOnCopilot, streamingMessage])

  // Always memoize, with or without the flag. It returns the same groups and keeps the same array
  // reference between renders when `messages` hasn't changed. That stable reference is what lets the
  // memoized messages below skip re-rendering while tokens stream.
  const groupedMessages = useMemo(() => groupChatMessages(messages), [messages])

  return {messages, groupedMessages}
}
